import { useEffect, useState } from 'react'
import { useStore } from './store/AppStore'
import { usePlayer } from './store/PlayerContext'
import Today from './screens/Today'
import Calendar from './screens/Calendar'
import Study from './screens/Study'
import Journey from './screens/Journey'
import Library from './screens/Library'
import Settings from './screens/Settings'
import { Button, Progress, hueVars } from './components/ui'
import { Mark } from './components/Mark'
import {
  IconCalendar,
  IconJourney,
  IconLibrary,
  IconPause,
  IconPlay,
  IconSettings,
  IconStudy,
  IconToday,
} from './components/icons'
import { fmtDate, today } from './lib/date'
import { onAccent } from './lib/color'
import { useI18n, useT } from './i18n'

export type Screen = 'today' | 'calendar' | 'study' | 'journey' | 'library' | 'settings'

const NAV: { id: Screen; labelKey: 'nav.today' | 'nav.calendar' | 'nav.study' | 'nav.journey' | 'nav.library'; Icon: typeof IconToday }[] = [
  { id: 'today', labelKey: 'nav.today', Icon: IconToday },
  { id: 'calendar', labelKey: 'nav.calendar', Icon: IconCalendar },
  { id: 'study', labelKey: 'nav.study', Icon: IconStudy },
  { id: 'journey', labelKey: 'nav.journey', Icon: IconJourney },
  { id: 'library', labelKey: 'nav.library', Icon: IconLibrary },
]

export default function App() {
  const { state } = useStore()
  const { settings } = state
  const { t, dir, lang } = useI18n()
  const [screen, setScreen] = useState<Screen>(() => readHash())
  const [toast, setToast] = useState<string | null>(null)

  /* --- theme: mode + accent applied to the document root ----------------- */
  useEffect(() => {
    const root = document.documentElement
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => {
      const dark =
        settings.themeMode === 'dark' || (settings.themeMode === 'system' && media.matches)
      root.dataset.theme = dark ? 'dark' : 'light'
    }
    apply()
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [settings.themeMode])

  /* Direction and language on <html> so the whole document flips, native form
     controls localise, and screen readers announce the right language. */
  useEffect(() => {
    const root = document.documentElement
    root.dir = dir
    root.lang = lang
  }, [dir, lang])

  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--accent', settings.accent)
    // Derived, not themed — see lib/color.ts.
    root.style.setProperty('--on-accent', onAccent(settings.accent))
  }, [settings.accent])

  /* --- routing: the hash keeps back/forward and refresh working ---------- */
  useEffect(() => {
    const onHash = () => setScreen(readHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const go = (next: Screen) => {
    window.location.hash = next
    setScreen(next)
    window.scrollTo({ top: 0 })
  }

  useEffect(() => {
    if (!toast) return
    const id = window.setTimeout(() => setToast(null), 2600)
    return () => window.clearTimeout(id)
  }, [toast])

  return (
    <div className="app">
      <nav className="nav" aria-label="Main">
        {NAV.map(({ id, labelKey, Icon }) => (
          <button
            key={id}
            className="nav__item"
            aria-current={screen === id ? 'page' : undefined}
            onClick={() => go(id)}
          >
            <Icon />
            <span>{t(labelKey)}</span>
          </button>
        ))}
        <button
          className="nav__item"
          aria-current={screen === 'settings' ? 'page' : undefined}
          onClick={() => go('settings')}
        >
          <IconSettings />
          <span>{t('nav.more')}</span>
        </button>
      </nav>

      <main className="app__main">
        <header className="topbar">
          <Mark />
          <div className="topbar__title">{t('app.tagline')}</div>
          <div className="topbar__spacer" />
          <div className="topbar__title">{fmtDate(today())}</div>
        </header>

        {screen === 'today' && <Today go={go} />}
        {screen === 'calendar' && <Calendar />}
        {screen === 'study' && <Study />}
        {screen === 'journey' && <Journey />}
        {screen === 'library' && <Library />}
        {screen === 'settings' && <Settings onToast={setToast} />}
      </main>

      <MiniPlayer hidden={screen === 'library'} />

      {toast && (
        <div className="toast" role="status">
          {toast}
        </div>
      )}
    </div>
  )
}

function readHash(): Screen {
  const h = window.location.hash.replace('#', '')
  const valid: Screen[] = ['today', 'calendar', 'study', 'journey', 'library', 'settings']
  return valid.includes(h as Screen) ? (h as Screen) : 'today'
}

/** Persistent transport bar — shown once something is playing, except on
 *  Library where the full track list already offers the same controls. */
function MiniPlayer({ hidden }: { hidden: boolean }) {
  const { current, playing, toggle, progress } = usePlayer()
  const t = useT()
  if (!current || hidden) return null
  return (
    <div className="player" style={hueVars(current.hue)}>
      <Button icon variant="primary" onClick={toggle} aria-label={t(playing ? 'player.pause' : 'player.play')}>
        {playing ? <IconPause /> : <IconPlay />}
      </Button>
      <div className="grow" style={{ minWidth: 0 }}>
        <div className="truncate" style={{ fontSize: 'var(--t-sm)', fontWeight: 500 }}>
          {current.title}
        </div>
        <div style={{ marginTop: 5 }}>
          <Progress value={progress} hue={current.hue} />
        </div>
      </div>
    </div>
  )
}
